import type { AuctisyncSupabase } from './client.js';

/**
 * The help corpus, read the same way by every surface that shows it.
 *
 * WHY THIS IS SHARED. `apps/help` built these queries inline in three route
 * components, and the native app — which is now growing an in-app help centre
 * rather than throwing bidders out to a browser — would otherwise have written
 * them a fourth time. The queries are not the interesting part; the RULES
 * inside them are:
 *
 *   - a TENANT's article beats the platform article when both carry the same
 *     slug, which is what white-labelling means (Module #33);
 *   - only `status = 'published'` is ever shown;
 *   - a surface picks its AUDIENCE, and a bidder-facing surface must not list
 *     the 55 auction-house staff articles the RLS policy happily returns.
 *
 * Two copies of "the tenant's version wins" is how one surface starts showing a
 * house's own wording while another shows AuctiSync's, for the same slug, on the
 * same day. One function is the only way that cannot happen.
 *
 * RLS DOES THE SECURITY, NOT THIS FILE. `help_articles_anon_read_public` and
 * `help_articles_authenticated_read_public` both restrict to published,
 * non-tier-gated PLATFORM rows; tenant rows need membership. Everything here is
 * about what is USEFUL to show, never about what is permitted.
 */

export interface HelpCategory {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  sort_order: number;
  audience: string;
}

export interface HelpArticleSummary {
  id: string;
  slug: string;
  title: string;
  summary: string;
  audience: string;
  tags: string[];
  published_at: string | null;
  view_count: number;
  category_id: string | null;
}

export interface HelpArticleDetail extends HelpArticleSummary {
  body_markdown: string;
  updated_at: string;
  /** Null for a platform article, set when a house has overridden this slug. */
  tenant_id: string | null;
}

const CATEGORY_COLUMNS = 'id, slug, title, description, sort_order, audience';
const SUMMARY_COLUMNS =
  'id, slug, title, summary, audience, tags, published_at, view_count, category_id';
const DETAIL_COLUMNS = `${SUMMARY_COLUMNS}, body_markdown, updated_at, tenant_id`;

/**
 * The audiences a BIDDER-facing surface shows.
 *
 * `all` is genuinely general (signing in, notifications, privacy); `bidder` is
 * written for them. Everything else on the platform is written for the auction
 * house running the sale, and the RLS policy returns it to anyone — a bidder
 * help centre listing "Managing failed payments" for staff would be the app
 * failing to choose, not the database failing to filter.
 */
export const BIDDER_AUDIENCES = ['all', 'bidder'] as const;

/**
 * Scope a help query to a tenant, or to the platform when there is none.
 *
 * White-label mode returns the platform corpus PLUS the house's own articles;
 * platform mode returns only the platform corpus. Written once because the
 * `.or(...)` string is the kind of thing that gets copied with a typo and then
 * silently returns every tenant's articles to everybody.
 */
function scopeToTenant<T>(
  query: T & {
    or: (filter: string) => T;
    is: (column: string, value: null) => T;
  },
  tenantId: string | null,
): T {
  return tenantId === null
    ? query.is('tenant_id', null)
    : query.or(`tenant_id.is.null,tenant_id.eq.${tenantId}`);
}

export interface HelpReadError {
  message: string;
}

/** Categories, in the order the corpus authors put them. */
export async function fetchHelpCategories(
  supabase: AuctisyncSupabase,
  tenantId: string | null = null,
): Promise<{ categories: HelpCategory[]; error?: HelpReadError }> {
  const query = supabase
    .from('help_categories')
    .select(CATEGORY_COLUMNS)
    .order('sort_order', { ascending: true });
  const res = await scopeToTenant(query, tenantId);
  if (res.error) return { categories: [], error: { message: res.error.message } };
  return { categories: (res.data ?? []) as HelpCategory[] };
}

export interface FetchHelpArticlesOptions {
  tenantId?: string | null;
  /**
   * Which audiences to list. OMITTED MEANS ALL OF THEM — the public help site
   * lists every audience on purpose, so a prospective auction house can read
   * the staff docs before signing up. A bidder-facing surface passes
   * {@link BIDDER_AUDIENCES} explicitly rather than relying on a default that
   * would quietly change what the website shows.
   */
  audiences?: readonly string[];
  /** Restrict to one category. */
  categoryId?: string;
  /**
   * `popular` — most-read first, which is what a help LANDING page wants.
   * `newest` — most recently published first, which is what a category listing
   * wants. Defaults to `popular`.
   */
  order?: 'popular' | 'newest';
}

/**
 * Every published article a surface should list, newest-first within a category.
 *
 * DEDUPED BY SLUG WITH THE TENANT WINNING. In white-label mode the same slug can
 * come back twice — the platform article and the house's override — and a list
 * showing both is a help centre that appears to contradict itself.
 */
export async function fetchHelpArticles(
  supabase: AuctisyncSupabase,
  options: FetchHelpArticlesOptions = {},
): Promise<{ articles: HelpArticleSummary[]; error?: HelpReadError }> {
  const tenantId = options.tenantId ?? null;

  let query = supabase
    .from('help_articles')
    .select(`${SUMMARY_COLUMNS}, tenant_id`)
    .eq('status', 'published');

  if (options.audiences !== undefined) query = query.in('audience', [...options.audiences]);
  if (options.categoryId !== undefined) query = query.eq('category_id', options.categoryId);

  // Tenant rows first, so the dedup below keeps the override.
  query = query.order('tenant_id', { ascending: false, nullsFirst: false });
  query =
    options.order === 'newest'
      ? query.order('published_at', { ascending: false })
      : query.order('view_count', { ascending: false });

  const res = await scopeToTenant(query, tenantId);
  if (res.error) return { articles: [], error: { message: res.error.message } };

  const seen = new Set<string>();
  const articles: HelpArticleSummary[] = [];
  for (const row of (res.data ?? []) as (HelpArticleSummary & { tenant_id: string | null })[]) {
    if (seen.has(row.slug)) continue;
    seen.add(row.slug);
    // Rebuilt field by field rather than rest-destructured. `tenant_id` is a
    // ROUTING detail of this read — which of two rows for a slug won — and
    // nothing downstream should be able to branch on it. (A `{ tenant_id: _, …rest }`
    // omit also trips CI's `--max-warnings 0` on the unused binding.)
    articles.push({
      id: row.id,
      slug: row.slug,
      title: row.title,
      summary: row.summary,
      audience: row.audience,
      tags: row.tags,
      published_at: row.published_at,
      view_count: row.view_count,
      category_id: row.category_id,
    });
  }
  return { articles };
}

/**
 * One category by slug, the house's own version winning over the platform's.
 */
export async function fetchHelpCategory(
  supabase: AuctisyncSupabase,
  slug: string,
  tenantId: string | null = null,
): Promise<{ category: HelpCategory | null; error?: HelpReadError }> {
  const query = supabase
    .from('help_categories')
    .select(CATEGORY_COLUMNS)
    .eq('slug', slug)
    .order('tenant_id', { ascending: false, nullsFirst: false })
    .limit(1);

  const res = await scopeToTenant(query, tenantId).maybeSingle();
  if (res.error) return { category: null, error: { message: res.error.message } };
  return { category: (res.data as HelpCategory | null) ?? null };
}

/**
 * One article by slug.
 *
 * `null` rather than an error when nothing matches: a dead slug is a content
 * problem, and the surface should say "we could not find that article" rather
 * than "something went wrong", which sends the reader to support.
 */
export async function fetchHelpArticle(
  supabase: AuctisyncSupabase,
  slug: string,
  tenantId: string | null = null,
): Promise<{ article: HelpArticleDetail | null; error?: HelpReadError }> {
  const query = supabase
    .from('help_articles')
    .select(DETAIL_COLUMNS)
    .eq('status', 'published')
    .eq('slug', slug)
    // The house's own version of a slug wins over AuctiSync's.
    .order('tenant_id', { ascending: false, nullsFirst: false })
    .limit(1);

  const res = await scopeToTenant(query, tenantId).maybeSingle();
  if (res.error) return { article: null, error: { message: res.error.message } };
  return { article: (res.data as HelpArticleDetail | null) ?? null };
}

/**
 * Bump the view counter. Fire-and-forget by design.
 *
 * A counter that fails must never cost the reader the article they opened, so
 * this swallows everything. It is also why it is a SECURITY DEFINER RPC rather
 * than an update: anon has no write grant on the table.
 */
export async function recordHelpArticleView(
  supabase: AuctisyncSupabase,
  slug: string,
): Promise<void> {
  try {
    await supabase.rpc('help_article_record_view', { article_slug: slug });
  } catch {
    // Deliberately silent — see above.
  }
}

/**
 * Filter a list of articles by what someone typed.
 *
 * PURE, AND IT RUNS ON THE LIST ALREADY LOADED. The bidder corpus is tens of
 * articles, not thousands, so a round trip per keystroke would buy nothing but
 * latency on a phone — and a search that only works online is a search that
 * fails in the exact places auctions happen. If the corpus ever outgrows this,
 * the replacement is a server-side text search, not a longer client filter.
 *
 * Matches title, summary and tags. NOT the body: a word buried in paragraph
 * nine ranks an article above one whose title is the question being asked.
 */
export function filterHelpArticles<
  T extends Pick<HelpArticleSummary, 'title' | 'summary' | 'tags'>,
>(articles: readonly T[], query: string): T[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [...articles];
  return articles.filter((a) => {
    if (a.title.toLowerCase().includes(needle)) return true;
    if (a.summary.toLowerCase().includes(needle)) return true;
    return a.tags.some((t) => t.toLowerCase().includes(needle));
  });
}

/**
 * The slug an in-corpus link points at, or null if the link leaves the corpus.
 *
 * Articles cross-reference each other as `/a/how-maximum-bids-work` — a path
 * that is meaningful on help.auctisync.com and meaningless inside an app. This
 * is what lets a native article view follow those links to the NEXT article
 * instead of kicking the reader into a browser mid-explanation.
 *
 * Anything else — an absolute URL, a mailto:, a path that is not `/a/…` — comes
 * back null and is opened externally, which is the honest treatment for a link
 * that genuinely leaves.
 */
export function helpSlugFromHref(href: string): string | null {
  const match = /^\/a\/([a-z0-9-]+)\/?$/i.exec(href.trim());
  return match?.[1] ?? null;
}

/**
 * "Was this helpful?"
 *
 * 🔴 NO PERSON IS RECORDED — not a user id, not a session. A vote is a fact
 * about an ARTICLE, and the standing rule is that buyer data does not go into
 * analytical pools. The tenant IS recorded, and that is not identity: a house
 * that has rewritten an article needs to know whether ITS version is the one
 * people are voting down.
 *
 * Fire-and-forget, like the view counter. A reader who pressed No has finished
 * with the question; an error about their vote failing would be the app talking
 * about itself at somebody looking for help.
 */
export async function recordHelpArticleFeedback(
  supabase: AuctisyncSupabase,
  slug: string,
  helpful: boolean,
  tenantId: string | null = null,
): Promise<void> {
  try {
    await supabase.rpc('record_help_article_feedback', {
      p_slug: slug,
      p_helpful: helpful,
      // The RPC's parameter is optional, so an absent tenant is OMITTED rather
      // than sent as null — PostgREST treats a null argument as a value and
      // would not fall through to the function's own default.
      ...(tenantId !== null ? { p_tenant_id: tenantId } : {}),
    });
  } catch {
    // Deliberately silent — see above.
  }
}

/** One article's tally, worst first. Super-admin only; the RPC enforces it. */
export interface HelpFeedbackRow {
  article_slug: string;
  title: string | null;
  helpful_count: number;
  unhelpful_count: number;
  total_count: number;
  last_at: string;
}

/**
 * What readers have said, over a window.
 *
 * Ordered by the UNHELPFUL count rather than by volume: the article most people
 * said did not help them is the one worth rewriting, and a list ordered by
 * traffic buries it under the most-read.
 */
export async function fetchHelpFeedbackSummary(
  supabase: AuctisyncSupabase,
  days = 90,
): Promise<{ rows: HelpFeedbackRow[]; error?: HelpReadError }> {
  const res = await supabase.rpc('help_feedback_summary', { p_days: days });
  if (res.error) return { rows: [], error: { message: res.error.message } };
  return { rows: (res.data ?? []) as HelpFeedbackRow[] };
}
